// Client-side ISBN validation (PRD barcode-scan §2.2).
//
// Why validate before calling the backend: a camera misread (or a fat-fingered
// manual entry) produces digits that look ISBN-shaped but fail the check digit.
// Catching that here saves a network round-trip to the ISBN metadata pipeline
// and lets the scanner keep looking instead of flashing a false "found".

/** Strip everything except 0-9 and X, upper-case the X. Mirrors index.jsx's
 *  normalizeIsbnInput so manual + scanned input share one canonical form. */
export const normalizeIsbn = (value) =>
  String(value || "").replace(/[^0-9Xx]/g, "").toUpperCase();

/**
 * ISBN-13 / EAN-13 checksum (modulo 10, alternating 1·3 weights).
 *
 * Each digit is multiplied by 1 or 3 (alternating, starting at 1); the total
 * must be divisible by 10. This is the format every physical book barcode
 * (Bookland EAN) actually encodes, so it's the hot path for the scanner.
 */
export const validateISBN13 = (isbn) => {
  const digits = normalizeIsbn(isbn);
  if (digits.length !== 13 || /[^0-9]/.test(digits)) return false;
  const sum = digits
    .split("")
    .reduce((acc, d, i) => acc + parseInt(d, 10) * (i % 2 === 0 ? 1 : 3), 0);
  return sum % 10 === 0;
};

/**
 * ISBN-10 checksum (modulo 11, weights 10..1, where a trailing 'X' === 10).
 *
 * TODO(seller-contribution): implement the real modulo-11 check.
 * Until then this only enforces the length so manual ISBN-10 entry isn't
 * blocked — but it will let invalid 10-char strings through.
 *
 * The algorithm: sum each of the first 9 digits times its weight (10 for the
 * first digit, descending to 2 for the ninth). Add the 10th "digit", which is
 * 0-9 OR the literal 'X' meaning 10. The ISBN is valid when that total is
 * divisible by 11. The 'X' (only ever the last character) is the gotcha most
 * naive implementations miss.
 */
export const validateISBN10 = (isbn) => {
  const chars = normalizeIsbn(isbn);
  if (chars.length !== 10) return false;
  // TODO: replace this length-only check with the modulo-11 sum described above.
  return true;
};

/** Accept either ISBN form. Used as the gate before hitting the backend. */
export const isValidIsbn = (value) => {
  const normalized = normalizeIsbn(value);
  if (normalized.length === 13) return validateISBN13(normalized);
  if (normalized.length === 10) return validateISBN10(normalized);
  return false;
};
